import "./AccountDetailComponent.scss";
import template from "./AccountDetailComponent.html";
import {IComponentOptions} from "angular";
import {Observable, ReplaySubject} from "rxjs";
import {EquityPositionViewModel, EquityPositionViewModels} from "../../models/EquityPositionViewModel";
import {isNotNull, sum} from "../../../../js/util/UtilFunctions";
import {EquityAccountId, EquityAccountRecord} from "../../types/EquityAccountRecord";
import {TradingAccountDetailService} from "../../../services/TradingAccountDetailService";
import {TradingAccountDetailsRecord, TradingAccountDetailsRecords} from "../../../types/TradingAccountDetailRecord";
import {formatAddress} from "./util/AccountDetailComponentUtil";
import {Pair, PairOf, Predicate} from "../../../../types/GenericTypes";
import {OrderViewStore} from "../../../../orders/store/view/OrderViewStore";
import {OrderViewModel, OrderViewModels} from "../../../../orders/models/OrderViewModel";
import {isConditionalIressOrderViewModel, isIressOrderModel} from "../../../../orders/models/iress/IressOrderViewModel";
import {
    isConditionalSimulatedOrderViewModel,
    isSimulatedOrderModel
} from "../../../../orders/models/simulated/SimulatedOrderViewModel";

export const AccountDetail = "tfAccountDetail";

export function accountDetail(): IComponentOptions {
    return {
        template,
        bindings: {
            account: "<",
            positions: "<"
        },
        controllerAs: "ctrl",
        controller: AccountDetailComponentController
    };
}

export class AccountDetailComponentController {

    /** @input */
    public set account(account: EquityAccountRecord | null) {
        if (account != null) {
            this.account$.next(account);
        }
    }

    private readonly account$ = new ReplaySubject<EquityAccountRecord>(1);

    /** @input */
    public set positions(positions: EquityPositionViewModels | null) {
        if (positions != null) {
            this.positions$.next(positions);
        }
    }

    private readonly positions$ = new ReplaySubject<EquityPositionViewModels>(1);
    private readonly tradingAccountDetails$: Observable<TradingAccountDetailsRecord>;
    private readonly mobilePhone$: Observable<string | null>;
    private readonly homePhone$: Observable<string | null>;
    private readonly workPhone$: Observable<string | null>;
    private readonly email$: Observable<string | null>;
    private readonly address$: Observable<string | null>;

    public readonly equityValue$: Observable<number>;
    public readonly cmaBalance$: Observable<number>;
    public readonly todayPnlTotal$: Observable<number>;
    public readonly todayPnlPercent$: Observable<number>;
    public readonly pendingSettlementsToday$: Observable<number>;
    public readonly pendingSettlementsTodayPlus1$: Observable<number>;
    public readonly pendingSettlementsTodayPlus2$: Observable<number>;
    public readonly pendingSettlementsTodayOther$: Observable<number>;
    public readonly pendingSettlementsTotal$: Observable<number>;
    public readonly openBuyOrders$: Observable<number>;
    public readonly openSellOrders$: Observable<number>;
    public readonly portfolioValue$: Observable<number>;
    public readonly availableFunds$: Observable<number>;

    /** @ngInject */
    public constructor(private readonly tradingAccountDetailService: TradingAccountDetailService,
                       private readonly orderViewStore: OrderViewStore) {
        this.equityValue$ = this.buildEquityValue();
        this.cmaBalance$ = this.buildCmaBalance();
        this.todayPnlTotal$ = this.buildTodayPnlTotal();
        this.todayPnlPercent$ = this.buildTodayPnlPercent();
        this.pendingSettlementsToday$ = this.buildPendingSettlementsToday();
        this.pendingSettlementsTodayPlus1$ = this.buildPendingSettlementsTodayPlus1();
        this.pendingSettlementsTodayPlus2$ = this.buildPendingSettlementsTodayPlus2();
        this.pendingSettlementsTodayOther$ = this.buildPendingSettlementsTodayOther();
        this.pendingSettlementsTotal$ = this.buildPendingSettlementsTotal();
        this.openBuyOrders$ = this.buildOpenBuyOrders();
        this.portfolioValue$ = this.buildPortfolioValue();
        this.availableFunds$ = this.buildAvailableFunds();

        this.tradingAccountDetails$ = this.account$
                                          .map((account) => account.id)
                                          .switchMap((account) => this.retrieveAccountDetails(account))
                                          .map((details) => details[0])
                                          .filter(isNotNull)
                                          .publishReplay(1)
                                          .refCount();

        this.mobilePhone$ = this.tradingAccountDetails$
                                .map((detail) => detail.tradingAccountDetail)
                                .map((detail) => detail.mobilePhone);

        this.workPhone$ = this.tradingAccountDetails$
                              .map((detail) => detail.tradingAccountDetail)
                              .map((detail) => detail.workPhone);

        this.homePhone$ = this.tradingAccountDetails$
                              .map((detail) => detail.tradingAccountDetail)
                              .map((detail) => detail.homePhone);

        this.email$ = this.tradingAccountDetails$
                          .map((detail) => detail.tradingAccountDetail)
                          .map((detail) => detail.emailAddress);

        this.address$ = this.tradingAccountDetails$
                            .map((detail) => detail.tradingAccountDetail)
                            .map(formatAddress);
    }

    private buildPortfolioValue(): Observable<number> {
        return Observable.combineLatest(this.equityValue$, this.cmaBalance$, this.account$)
                         .map(([equityValue, cmaBalance, acc]) => equityValue + cmaBalance + acc.ledgerAmount + acc.etoLedgerAmount)
                         .startWith(0)
                         .publishReplay(1)
                         .refCount();
    }

    private buildEquityValue(): Observable<number> {
        return this.positions$
                   .map((pos) => this.totalPositionValue(pos))
                   .startWith(0)
                   .publishReplay(1)
                   .refCount();
    }

    private totalPositionValue(positions: EquityPositionViewModels): number {
        return positions.map((p) => p.totalValue)
                        .filter(isNotNull)
                        .reduce(sum, 0);
    }

    private buildAvailableFunds(): Observable<number> {
        return Observable.combineLatest(this.account$, this.openBuyOrders$)
                         .map(([acc, openBuyOrders]) => acc.cmaBalance + acc.ledgerAmount - openBuyOrders)
                         .startWith(0)
                         .publishReplay(1)
                         .refCount();
    }

    private buildCmaBalance(): Observable<number> {
        return this.account$
                   .map((acc) => acc.cmaBalance)
                   .startWith(0)
                   .publishReplay(1)
                   .refCount();
    }

    private buildTodayPnlTotal(): Observable<number> {
        return this.positions$
                   .map((positions) => this.calculateTotalTodayPnl(positions))
                   .startWith(0)
                   .publishReplay(1)
                   .refCount();
    }

    private calculateTotalTodayPnl(positions: EquityPositionViewModels): number {
        return positions.map((position) => position.todayPnL)
                        .reduce(sum, 0);
    }

    private buildTodayPnlPercent(): Observable<number> {
        return this.positions$
                   .map((positions) => this.calculateTodayPnlPercent(positions))
                   .startWith(0)
                   .publishReplay(1)
                   .refCount();
    }

    private calculateTodayPnlPercent(positions: EquityPositionViewModels): number {
        const totals = positions.reduce((acc, next) => this.accumulateTotals(acc, next), PairOf(0, 0));
        return (totals[0] - totals[1]) / totals[1];
    }

    private accumulateTotals(totals: Pair<number>, next: EquityPositionViewModel): Pair<number> {
        if ((next.eodValue == null) || (next.totalValue == null)) {
            return totals;
        } else {
            const currentValue = totals[0] + next.totalValue;
            const eodValue = totals[1] + next.eodValue;
            return PairOf(currentValue, eodValue);
        }
    }

    private buildPendingSettlementsTotal(): Observable<number> {
        const sources = [
            this.pendingSettlementsToday$,
            this.pendingSettlementsTodayPlus1$,
            this.pendingSettlementsTodayPlus2$,
            this.pendingSettlementsTodayOther$
        ];
        return Observable.combineLatest(sources)
                         .map(([today, todayPlus1, todayPlus2, other]) => today + todayPlus1 + todayPlus2 + other)
                         .startWith(0)
                         .publishReplay(1)
                         .refCount();
    }

    private buildPendingSettlementsToday(): Observable<number> {
        return this.account$
                   .map((account) => account.unsettledCash.tradingDay)
                   .startWith(0)
                   .publishReplay(1)
                   .refCount();
    }

    private buildPendingSettlementsTodayPlus1(): Observable<number> {
        return this.account$
                   .map((account) => account.unsettledCash.tradingDayPlus1 + account.etoNewBusiness)
                   .startWith(0)
                   .publishReplay(1)
                   .refCount();
    }

    private buildPendingSettlementsTodayPlus2(): Observable<number> {
        return this.account$
                   .map((account) => account.unsettledCash.tradingDayPlus2 + account.equityNewBusiness)
                   .startWith(0)
                   .publishReplay(1)
                   .refCount();
    }

    private buildPendingSettlementsTodayOther(): Observable<number> {
        return this.account$
                   .map((account) => account.unsettledCash.tradingOtherCash)
                   .startWith(0)
                   .publishReplay(1)
                   .refCount();
    }

    private buildOpenBuyOrders(): Observable<number> {
        return this.currentPortfolioOrdersTotal((o) => this.isOpenBuy(o) && !this.isConditional(o));
    }

    private isOpenBuy(order: OrderViewModel): boolean {
        return order.isOpen && order.isBuy;
    }

    private isConditional(order: OrderViewModel): boolean {
        return (isIressOrderModel(order) && isConditionalIressOrderViewModel(order))
            || (isSimulatedOrderModel(order) && isConditionalSimulatedOrderViewModel(order));
    }

    private currentPortfolioOrdersTotal(predicate: Predicate<OrderViewModel>): Observable<number> {
        return this.orderViewStore
                   .currentPortfolioOrders
                   .map((orders) => orders.filter(predicate))
                   .map((orders) => this.calculateOrdersTotal(orders))
                   .startWith(0)
                   .publishReplay(1)
                   .refCount();
    }

    private calculateOrdersTotal(orders: OrderViewModels): number {
        return orders.map((o) => o.quantity * (o.limit || 0))
                     .reduce(sum, 0);
    }

    private retrieveAccountDetails(accountId: EquityAccountId): Observable<TradingAccountDetailsRecords> {
        return Observable.of(accountId)
                         .flatMap((i) => this.tradingAccountDetailService.detail(accountId))
                         .catch((err) => Observable.empty());
    }
}
